import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Input from '../../common/form/Input'
import SimplePhotoUploader from '../../common/SimplePhotoUploader'
import SubmitButton from '../../common/buttons/SubmitButton'
import {
  resetCourseCategoryErrors,
  selectHasCategories,
  setCategoriesOrdered,
  setCourseCategory,
  setField,
  setHeaderImage,
} from '../../../reducers/community-settings/landingPageSettingsReducer'
import { updateCommunityLandingPageSettings } from '../../../actions/communitiesActions'

import classes from './CommunityLandingPageTab.module.scss'
import ConfirmationModal from './ConfirmationModal'
import CourseCategoryEditModal from './CourseCategoryEditModal'
import CategoriesReorderingDnd from './CategoriesReorderingDnd'
import CancelButton from '../../common/buttons/CancelButton'

const NEW_COURSE_CATEGORY = {
  name: '',
  color: '',
  order: null,
  description: '',
  headerImageUrl: '',
  headerImageData: null,
  headerImageName: '',
}

const SettingsForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { community, settingsFields, attributeErrors, categoriesOrdered } = useSelector(state => state.communitySettings.landingPage)
  const hasCategories = useSelector(selectHasCategories)

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [isCourseCategoryModalOpen, setCourseCategoryModalOpen] = useState(false)
  const [isReorderingActive, setReorderingActive] = useState(false)

  const landingPageUrl = `${process.env.REACT_APP_BASE_URL}/landing/${community.id}`

  const handleInputChange = (e) => {
    const field = e.target.name
    const value = e.target.value

    dispatch(setField({ field, value }))
  }

  const handlePhotoChange = (photoData) => {
    const { uploadPhotoFile, uploadPhotoSrc } = photoData
    dispatch(setHeaderImage({
      headerImageUrl: uploadPhotoSrc,
      headerImageData: uploadPhotoFile,
      headerImageName: uploadPhotoFile?.name,
    }))
  }

  const handleOpenEditCategoryModal = (category = NEW_COURSE_CATEGORY) => {
    dispatch(setCourseCategory(category))
    setCourseCategoryModalOpen(true)
  }

  const handleCloseCourseCategoryModal = () => {
    dispatch(resetCourseCategoryErrors())
    setCourseCategoryModalOpen(false)
  }

  const handleReorderCategories = () => {
    dispatch(setCategoriesOrdered(settingsFields.categories))
    setReorderingActive(true)
  }

  const handleConfirmNewOrder = () => {
    setReorderingActive(false)
    dispatch(setField({ field: 'categories', value: categoriesOrdered }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    dispatch(updateCommunityLandingPageSettings({
      id: community.id,
      communityData: settingsFields,
    }))
      .unwrap()
      .then(() => setConfirmationModalOpen(true))
  }

  return (
    <>
      <form id='landingPageSettings' className={classes['settings-form']} onSubmit={handleSubmit}>
        <div className='px-3 pb-4 d-flex flex-column'>
          <label htmlFor='landingPageUrl'>{t('communitySettings.landingPage.settings.linkToCommunityLandingPage')}</label>
          <a id='landingPageUrl' className={classes['link']} href={landingPageUrl} target='_blank'>{landingPageUrl}</a>
        </div>
        <div className='px-3 pb-4'>
          <label htmlFor='description' className='d-flex justify-content-between align-items-center'>
            {t('communitySettings.landingPage.settings.communityDescription')}
          </label>
          <p className={classnames('mb-2', classes['label-description'])}>
            {t('communitySettings.landingPage.settings.descriptionEmptyHelp')}
          </p>
          <Input
            id='description'
            name='description'
            type='textarea'
            placeholder={t('communitySettings.landingPage.settings.writeADescription')}
            value={settingsFields.description}
            onChange={handleInputChange}
            styles={{ inputContainer: { height: '160px' } }}
          />
        </div>
        <div className='px-3 pb-4'>
          <label htmlFor='input-photo-upload'>{t('communitySettings.landingPage.settings.landingPageHeaderImage')}</label>
          <SimplePhotoUploader
            className='form-control photo-select'
            sendPhotoData={handlePhotoChange}
            selectedPhoto={settingsFields.headerImageUrl}
            placeholder={t('communitySettings.landingPage.settings.uploadAPhoto')}
            error={attributeErrors?.headerImagePhoto[0]}
          />
        </div>
        <div className='px-3 pb-4'>
          <label htmlFor='bookingUrl'>{t('communitySettings.landingPage.settings.facilityPrivateLessonBookingLink')}</label>
          <p className={classnames('mb-2', classes['label-description'])}>
            {t('communitySettings.landingPage.settings.bookingLinkHelp')}
          </p>
          <Input
            id='bookingUrl'
            name='bookingUrl'
            type='text'
            placeholder={t('communitySettings.landingPage.settings.provideABookingLink')}
            value={settingsFields.bookingUrl}
            onChange={handleInputChange}
          />
        </div>
        <div className='px-3 pb-3 overflow-hidden'>
          <label htmlFor='bookingUrl'>{t('communitySettings.landingPage.settings.programCategories')}</label>
          <p className={classnames('mb-4', classes['label-description'])}>
            {t('communitySettings.landingPage.settings.programCategoriesHelp')}
          </p>
          {hasCategories ? (
            <CategoriesReorderingDnd
              isReorderingActive={isReorderingActive}
              categories={isReorderingActive ? categoriesOrdered : settingsFields.categories}
              handleEditButtonClick={handleOpenEditCategoryModal}
            />
          ) : (
            <div className={classnames('mb-3 d-flex justify-content-center align-items-center', classes['placeholder'])}>
              {t('communitySettings.landingPage.settings.noAvailableCategories')}
            </div>
          )}
        </div>
        <div className='px-3 d-flex justify-content-between'>
          {isReorderingActive ? (
            <>
              <CancelButton
                type='button'
                buttonMessage={t('common.cancel')}
                className={classes['left-button']}
                onClick={() => setReorderingActive(false)}
              />
              <SubmitButton
                noTopMargin
                type='button'
                buttonMessage={t('communitySettings.landingPage.settings.saveNewOrder')}
                className={classes['right-button']}
                onClick={handleConfirmNewOrder}
              />
            </>
          ) : (
            <>
              <SubmitButton
                noTopMargin
                type='button'
                buttonMessage={t('communitySettings.landingPage.settings.createNewCategory')}
                iconType='addButton'
                className={classes['left-button']}
                onClick={() => {
                  handleOpenEditCategoryModal()
                }}
              />
              {hasCategories && (
                <SubmitButton
                  noTopMargin
                  type='button'
                  buttonMessage={t('communitySettings.landingPage.settings.reorderCategories')}
                  className={classes['right-button']}
                  onClick={handleReorderCategories}
                />
              )}
            </>
          )}
        </div>
      </form>
      <ConfirmationModal isOpen={isConfirmationModalOpen} onClose={() => setConfirmationModalOpen(false)} />
      <CourseCategoryEditModal
        isOpen={isCourseCategoryModalOpen}
        onClose={handleCloseCourseCategoryModal}
        communityId={community.id}
      />
    </>
  )
}

export default SettingsForm
